import { emptyFragment } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { Search } from 'lucide-react';
import { Maybe } from 'maybeasy';
import * as React from 'react';
import { useState } from 'react';
import { T, translation, TranslationsContext } from '../../../../../Translations';
import { programCategoryLabel } from '../../../../Licensing/ProgramCategories';
import { CategoryIcon } from './CategoryBadge';
import { CourseOption, groupByCategory, matchesQuery, runChoice } from './CourseOptions';
import WhenItStarts, { RunChooser } from './WhenItStarts';

// Where the open catalog goes. On the invite page it sits in the flow and
// pushes the participant box down; in the Share overlay it floats over the
// rest of the dialog so the dialog keeps its size (DVLP-7559, DVLP-8060).
export type CatalogLayout = 'inline' | 'floating';

interface Props {
  options: ReadonlyArray<CourseOption>;
  selectedKey: Maybe<string>;
  layout: CatalogLayout;
  chooser: RunChooser;
  onPick: (option: CourseOption) => void;
  onClose: () => void;
}

const rowClassName = (selected: boolean) =>
  `flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left text-sm hover:bg-secondary ${
    selected ? 'bg-secondary' : ''
  }`;

const frameClassName = (layout: CatalogLayout): string => {
  const frame = 'mt-2 overflow-hidden rounded-lg border border-border bg-card shadow-md';
  switch (layout) {
    case 'inline':
      return frame;
    case 'floating':
      return `absolute left-0 right-0 z-50 ${frame}`;
    default:
      return assertNever(layout);
  }
};

// The floating list is shorter so it hangs as little as possible below the
// dialog it floats over.
const listClassName = (layout: CatalogLayout): string => {
  switch (layout) {
    case 'inline':
      return 'max-h-96 overflow-y-auto py-1';
    case 'floating':
      return 'max-h-64 overflow-y-auto py-1';
    default:
      return assertNever(layout);
  }
};

// The collection's courses grouped by type, searchable by course or school.
// What picking a row means is the caller's: the invite page aims at the
// course's earliest run, the Share overlay at the course itself.
function CourseCatalog({ options, selectedKey, layout, chooser, onPick, onClose }: Props) {
  const ts = React.useContext(TranslationsContext);
  const [query, setQuery] = useState('');
  const groups = groupByCategory(options.filter((option) => matchesQuery(option, query)));
  const isSelected = (option: CourseOption) =>
    selectedKey.map((key) => key === option.key).getOrElseValue(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  return (
    <div
      className={frameClassName(layout)}
      onKeyDown={handleKeyDown}
      data-intercom-target="share-course-catalog"
    >
      <div className="relative border-b border-border">
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={translation('Search courses')(ts)}
          aria-label={translation('Search courses')(ts)}
          className="w-full bg-card py-3 pl-10 pr-4 focus:outline-none"
          style={{ fontSize: 'var(--text-sm)' }}
          data-intercom-target="share-course-search"
        />
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>

      <div className={listClassName(layout)}>
        {groups.map((group) => (
          <div key={group.category}>
            <div
              className="flex items-center gap-2 px-4 pb-1 pt-3 uppercase tracking-wide text-muted-foreground"
              style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}
            >
              <CategoryIcon category={group.category} />
              <T kind={programCategoryLabel(group.category)} />
            </div>
            {group.options.map((option) => {
              const selected = isSelected(option);
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => onPick(option)}
                  className={rowClassName(selected)}
                  aria-current={selected}
                  data-intercom-target="share-course-option"
                >
                  <span className="min-w-0 pl-6">
                    <span
                      className="block truncate"
                      style={{ fontWeight: 'var(--font-weight-medium)' }}
                      title={option.family.name}
                    >
                      {option.family.name}
                    </span>
                    {option.family.schoolName
                      .map((school) => (
                        <span
                          key="school"
                          className="block truncate text-muted-foreground"
                          style={{ fontSize: 'var(--text-xs)' }}
                          title={school}
                        >
                          {school}
                        </span>
                      ))
                      .getOrElse(emptyFragment)}
                  </span>
                  <span
                    className="flex-none text-muted-foreground"
                    style={{ fontSize: 'var(--text-xs)' }}
                  >
                    <WhenItStarts choice={runChoice(option)} chooser={chooser} />
                  </span>
                </button>
              );
            })}
          </div>
        ))}

        {groups.length === 0 && (
          <p
            className="px-4 py-6 text-center text-muted-foreground"
            style={{ fontSize: 'var(--text-sm)' }}
          >
            <T kind="No courses match your search." />
          </p>
        )}
      </div>
    </div>
  );
}

export default CourseCatalog;
